import { useEffect, useState } from "react";
import { Icon } from "./Icon.js";

export const GCP_TEST_USERS_URL = "https://console.cloud.google.com/auth/audience";
export const GCP_PROJECT_NAME = "appsdo-calsync";

const STORAGE_KEY = "calsync.howToApprove.collapsed";

interface HowToApproveProps {
  /** When provided, step 4 highlights this email + offers a copy button. */
  highlightEmail?: string;
  /** Reference variant: collapsible header + persisted state. Modal variant: always open. */
  variant?: "reference" | "modal";
}

/**
 * The 5-step recipe an admin follows in Google Cloud Console before clicking
 * "Approve" on a Gmail request. Reused by:
 *   - the Approvals page (collapsible reference panel, always visible)
 *   - the Approve modal (always-open, with the specific email pre-filled)
 */
export function HowToApprove({ highlightEmail, variant = "reference" }: HowToApproveProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (variant === "modal") return false;
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  });

  useEffect(() => {
    if (variant === "reference") {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    }
  }, [collapsed, variant]);

  return (
    <div className="bg-surface-container border border-outline-variant rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => variant === "reference" && setCollapsed((v) => !v)}
        className={`w-full px-md py-md flex items-center justify-between text-left ${
          variant === "reference" ? "hover:bg-surface-container-high transition-colors cursor-pointer" : "cursor-default"
        }`}
      >
        <div className="flex items-center gap-3">
          <Icon name="help" className="text-primary-fixed-dim" />
          <div>
            <h2 className="text-headline-md text-on-surface">
              How to approve a Gmail request
            </h2>
            <p className="text-body-sm text-on-surface-variant">
              5 quick steps in Google Cloud Console — needed once per email until we get the app verified.
            </p>
          </div>
        </div>
        {variant === "reference" && (
          <Icon
            name="expand_more"
            className={`text-on-surface-variant transition-transform ${collapsed ? "" : "rotate-180"}`}
          />
        )}
      </button>

      {!collapsed && (
        <div className="px-md pb-md pt-0 border-t border-outline-variant/30">
          <ol className="space-y-3 mt-md">
            <Step n={1}>
              <p>
                Open the Google Cloud Auth Audience page (use the AppsDo Google account):
              </p>
              <a
                href={GCP_TEST_USERS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 px-3 py-1.5 bg-surface-container-low border border-outline-variant rounded text-label-xs font-mono uppercase text-primary-fixed-dim hover:bg-surface-container transition-colors"
              >
                <Icon name="open_in_new" className="text-[14px]" />
                Open in new tab
              </a>
            </Step>
            <Step n={2}>
              <p>
                Top-bar project picker → make sure{" "}
                <span className="font-mono text-primary-fixed-dim">{GCP_PROJECT_NAME}</span> is selected.
              </p>
            </Step>
            <Step n={3}>
              <p>
                Scroll to <span className="font-mono">Test users</span> → click{" "}
                <span className="font-mono">+ Add users</span>.
              </p>
            </Step>
            <Step n={4}>
              {highlightEmail ? (
                <>
                  <p>Paste this email into the form:</p>
                  <CopyEmail email={highlightEmail} />
                </>
              ) : (
                <p>
                  Paste the colleague's Gmail address into the form (you'll find it on the request row in the
                  list below — there's a copy button on each Approve dialog).
                </p>
              )}
            </Step>
            <Step n={5}>
              <p>
                Click <span className="font-mono">Save</span> in Google Cloud, then come back here and click the
                green <span className="font-mono">Approve</span> button on the matching request.
              </p>
            </Step>
          </ol>

          <div className="mt-md p-sm bg-tertiary-container/10 border border-tertiary-container/30 rounded text-body-sm flex items-start gap-2">
            <Icon name="lightbulb" className="text-tertiary-container text-[18px] mt-0.5" />
            <p className="text-on-surface-variant flex-1">
              <span className="text-tertiary-container font-semibold">Heads up:</span> this step disappears entirely
              once we submit the OAuth app for Google verification (free, ~1–4 weeks). For now we're stuck adding
              each new colleague manually.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <div className="w-7 h-7 rounded-full bg-primary-container/20 border border-primary-fixed-dim/30 flex items-center justify-center text-label-xs font-mono text-primary-fixed-dim shrink-0">
        {n}
      </div>
      <div className="flex-1 text-body-sm text-on-surface">{children}</div>
    </li>
  );
}

function CopyEmail({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  }
  return (
    <button
      onClick={copy}
      className="mt-2 w-full text-left px-3 py-2 bg-surface-container-lowest border border-outline-variant rounded font-mono text-body-sm text-primary-fixed-dim hover:border-primary-fixed-dim flex items-center justify-between gap-2 transition-colors"
    >
      <span className="truncate">{email}</span>
      <Icon name={copied ? "check" : "content_copy"} className="text-[16px] shrink-0" />
    </button>
  );
}
